import { CSSProperties, useEffect, useMemo, useState, type UIEvent } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Spin } from "antd";
import clsx from "clsx";
import { useDebounce } from "@/shared/utils";
import { UserInfo, projectQueries } from "@entities/project";
import { UserSuggestions } from "@entities/user";
import { DropdownLoader } from "./dropdown-loader";

import "./user-infinite-scroll.scss";

interface UsersInfiniteScrollProps {
  projectId: string;
  projectParticipantId?: string;
  onUserSelect: (company: UserInfo) => void;
  style?: CSSProperties;
  className?: string;
  clearTrigger?: number;
}

export function UsersInfiniteScroll({
  projectId,
  projectParticipantId,
  onUserSelect,
  style,
  className,
  clearTrigger = 0,
}: UsersInfiniteScrollProps) {
  const [email, setEmail] = useState<string | undefined>(undefined);
  const searchEmail = useDebounce(email, 300);
  
  useEffect(() => {
    if (clearTrigger > 0) {
      setEmail(undefined);
    }
  }, [clearTrigger]);
  
  const {
    data: response,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery({
    ...projectQueries.getOrganizationUserInfinite({
      projectId,
      projectParticipantId,
      limit: 10,
      // eslint-disable-next-line no-nested-ternary
      userEmail: searchEmail?.includes("$test") ? "" : searchEmail?.includes("@") ? searchEmail : undefined,
    }),
  });

  const users = useMemo(() => {
    if (response?.pages) {
      return response.pages.flatMap((page) => page.userInfo);
    }
    return [];
  }, [response?.pages]);

  const onChange = (value: string) => {
    setEmail(value);
  };

  const onPopupScroll = async (e: UIEvent<HTMLDivElement>) => {
    const element = e.target as HTMLDivElement;
    if (element.scrollTop + element.offsetHeight >= element.scrollHeight - 60) {
      await fetchNextPage();
    }
  };

  return (
    <>
    <UserSuggestions
      className={clsx("user-infinite-scroll", className)}
      onSearch={onChange}
      value={email}
      style={style}
      popupMatchSelectWidth={false}
      placement="bottomLeft"
      users={users}
      onUserSelect={onUserSelect}
      onPopupScroll={onPopupScroll}
      dropdownRender={(menu) =>
        DropdownLoader({
          menu,
          isLoading: isFetchingNextPage,
        })
      }
    />
    {
      (() => {
        const isSearching = email?.includes("@") && (email !== searchEmail || isFetching);
        const isNotFound = email?.includes("@") && email === searchEmail && !isFetching && !users.length;
        if (isSearching) {
          return <div className="user-infinite-scroll-loading"><Spin /></div>;
        }
        if (isNotFound) {
          return (
            <div className="user-infinite-scroll-not-found">
              Пользователь не найден: либо пользователь не добавлен в сотрудники организации, либо организация не является участником проекта. Проверьте адрес электронной почты
            </div>
          );
        }
        return null;
      })()
    }
    </>
  );
}
